import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useChatbots } from "../contexts/ChatbotContext";
import Layout from "../components/Layout";
import DirectoryTree from "../components/DirectoryTree";
import AccessControl from "../components/AccessControl";
import FunctionSelector from "../components/FunctionSelector";
import config from "../config";
import "./CreatePage.css";
import downCaret from "../public/img/down_caret.svg";

const CreatePage = () => {
  const navigate = useNavigate();
  const { refreshChatbots, getAllChatbots } = useChatbots();

  const [formType, setFormType] = useState("chatbot");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [showDirectoryTree, setShowDirectoryTree] = useState(false);
  const directorySelectorRef = useRef(null);
  const initialTemperature = 1.0; // Default initial temperature for new tools

  // Form fields
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    directory: false,
    parent: "",
    icon: "",
    tags: [],
    access: ["All"],
    provider: "openai",
    model: "",
    temperature: 1.0,
    prompt: "",
    functions: [],
    starter_questions: [],
    folder: "",
  });

  // Get all chatbots for directory tree
  const allChatbots = getAllChatbots ? getAllChatbots() : [];

  // Get the selected parent directory name
  const getSelectedDirectoryName = () => {
    if (!formData.parent) {
      return "Root (No Parent)";
    }

    const selectedDirectory = allChatbots.find(
      (chatbot) => chatbot._id === formData.parent
    );
    return selectedDirectory
      ? selectedDirectory.title
      : "Select a parent directory";
  };

  // Tag input
  const [tagInput, setTagInput] = useState("");

  // Starter question input
  const [questionInput, setQuestionInput] = useState("");


  // Fetch available models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.dashboard.models}`,
          {
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (data.success) {
          setAvailableModels(data.data);
          // Default to the top-ranked model of the selected provider
          // (the API returns models rank-sorted).
          setFormData((prev) => {
            if (prev.model) return prev;
            const first = (data.data || []).find(
              (m) =>
                m.provider === prev.provider &&
                (m.public === undefined || m.public === true)
            );
            return first ? { ...prev, model: first.id } : prev;
          });
        }
      } catch (error) {
        console.error("Failed to fetch models:", error);
        setErrorMessage(
          "Failed to load available models. Please try again later."
        );
      }
    };

    fetchModels();
  }, []);

  // Handle GPT-5 models - set temperature to 1 and disable field (except GPT-5.2)
  useEffect(() => {
    const modelLower = formData.model ? formData.model.toLowerCase() : '';
    const isGPT5Model = modelLower && (
      (modelLower.includes('gpt-5') && !modelLower.includes('gpt-5.2')) ||
      modelLower === 'o1' ||
      modelLower.includes('o1-')
    );
    
    if (isGPT5Model) {
      setFormData((prev) => ({
        ...prev,
        temperature: 1.0,
      }));
    }
  }, [formData.model]);

  // Handle form type change
  const handleFormTypeChange = (type) => {
    setFormType(type);
    setFormData((prev) => ({
      ...prev,
      directory: type === "directory",
    }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle temperature slider specially to maintain precision
    if (name === "temperature") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseFloat(value),
      }));
    } else if (name === "model") {
      // When the model select changes, the 'value' will be the model's string ID (e.g., "gpt-4")
      // This assumes the <select> <option> value attribute is set to the model's string identifier
      setFormData((prev) => ({
        ...prev,
        model: value,
      }));
    } else if (name === "provider") {
      // Switching provider re-defaults to that provider's top-ranked model.
      setFormData((prev) => {
        const first = availableModels.find(
          (m) =>
            m.provider === value &&
            (m.public === undefined || m.public === true)
        );
        return { ...prev, provider: value, model: first ? first.id : "" };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle directory selection (single click - don't close)
  const handleDirectorySelect = (directoryId) => {
    setFormData((prev) => ({
      ...prev,
      parent: directoryId,
    }));
    // Don't close the tree on single click
  };

  // Close directory tree
  const handleCloseDirectoryTree = () => {
    setShowDirectoryTree(false);
  };

  // Toggle directory tree visibility
  const toggleDirectoryTree = () => {
    setShowDirectoryTree((prev) => !prev);
  };

  // Handle click outside to close directory tree
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (directorySelectorRef.current && !directorySelectorRef.current.contains(event.target)) {
        setShowDirectoryTree(false);
      }
    };

    if (showDirectoryTree) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDirectoryTree]);

  // Handle access control changes
  const handleAccessChange = useCallback((accessList) => {
    setFormData((prev) => ({
      ...prev,
      access: accessList,
    }));
  }, []);

  // Handle tag input
  const handleAddTag = () => {
    if (tagInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  // Handle tag removal
  const handleRemoveTag = (index) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  // Handle starter question input
  const handleAddQuestion = () => {
    if (questionInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        starter_questions: [...prev.starter_questions, questionInput.trim()],
      }));
      setQuestionInput("");
    }
  };

  // Handle question removal
  const handleRemoveQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      starter_questions: prev.starter_questions.filter((_, i) => i !== index),
    }));
  };

  // Function handling
  const handleFunctionChange = (selectedFunctions) => {
    setFormData((prev) => ({
      ...prev,
      functions: selectedFunctions,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Create a copy of formData to submit
      const formDataToSubmit = { ...formData };

      // Ensure access is properly formatted - if it's empty, default to "All"
      if (!formDataToSubmit.access || formDataToSubmit.access.length === 0) {
        formDataToSubmit.access = ["All"];
      }

      // Robust Google Drive folder URL parsing (handles query params like ?usp=drive_link)
      const extractDriveFolderId = (val) => {
        if (!val || typeof val !== "string") return val;
        const trimmed = val.trim();
        try {
          const u = new URL(trimmed);
          const parts = u.pathname.split("/").filter(Boolean);
          const idx = parts.indexOf("folders");
          if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
          const qp = u.searchParams.get("id");
          if (qp) return qp;
          return trimmed;
        } catch (e) {
          // Fallback for non-URL strings; last path segment if pattern matches
          if (trimmed.includes("/folders/")) {
            const segs = trimmed.split("/");
            return segs[segs.length - 1];
          }
          return trimmed;
        }
      };

      if (formDataToSubmit.folder) {
        formDataToSubmit.folder = extractDriveFolderId(formDataToSubmit.folder);
      }

      // Debug logging
      console.log("Submitting chatbot data:", formDataToSubmit);

      const response = await fetch(
        `${config.api.baseUrl}/dashboard/create-chatbot`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify(formDataToSubmit),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccessMessage("Tool created successfully!");
        // Refresh chatbots list to include the new item
        refreshChatbots();

        // Determine new chatbot ID from response payload
        const newId =
          (data && data.data && data.data._id) || data.chatbot_id || data.id;
        if (newId) {
          const path = formData.directory ? "/directory" : "/chat";
          navigate(`${path}?id=${newId}`);
        } else {
          // Fallback if ID missing
          setErrorMessage("Created, but could not determine new chatbot ID.");
        }
      } else {
        setErrorMessage(
          data.error || "Failed to create tool. Please try again."
        );
      }
    } catch (error) {
      console.error("Error creating tool:", error);
      setErrorMessage(
        "An error occurred while creating the tool. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Add handler to prevent form submission on Enter key
  const handleKeyDown = (e) => {
    // Prevent form submission when Enter key is pressed in any input field
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      e.preventDefault();
    }
  };

  return (
    <Layout>
      <div className="create-page">
        <div className="create-container">
          <h1 className="create-title">Create New Tool</h1>

          <div className="form-type-selector">
            <button
              className={`form-type-button ${
                formType === "chatbot" ? "active" : ""
              }`}
              onClick={() => handleFormTypeChange("chatbot")}
            >
              Chatbot
            </button>
            <button
              className={`form-type-button ${
                formType === "directory" ? "active" : ""
              }`}
              onClick={() => handleFormTypeChange("directory")}
            >
              Directory
            </button>
          </div>

          {errorMessage && <div className="error-message">{errorMessage}</div>}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          <form
            onSubmit={handleSubmit}
            className="create-form"
            onKeyDown={handleKeyDown}
          >
            <div className="form-section">
              <h2>Basic Information</h2>
              <div className="form-section-columns">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter title"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="parent">Parent Directory *</label>
                <div className="directory-selector" ref={directorySelectorRef}>
                  <div
                    className="selected-directory"
                    onClick={toggleDirectoryTree}
                  >
                    <span>{getSelectedDirectoryName()}</span>
                    <span className="directory-selector-toggle">
                      <img
                        src={downCaret}
                        alt="toggle"
                        className={`dir-caret ${
                          showDirectoryTree ? "open" : ""
                        }`}
                      />
                    </span>
                  </div>

                  {showDirectoryTree && (
                    <div className="directory-tree-wrapper" onClick={(e) => e.stopPropagation()}>
                      <DirectoryTree
                        chatbots={allChatbots}
                        selectedDirectoryId={formData.parent}
                        onSelectDirectory={handleDirectorySelect}
                        onClose={handleCloseDirectoryTree}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="access">Access Control *</label>
                <AccessControl
                  value={formData.access || ["All"]}
                  onChange={handleAccessChange}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="tags">Tags</label>
                <div className="tag-input-container">
                  <input
                    type="text"
                    id="tagInput"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="add-button"
                  >
                    Add
                  </button>
                </div>
                <div className="tags-container">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button
                        type="button"
                        className="remove-tag"
                        onClick={() => handleRemoveTag(index)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {!formData.directory && (
              <div className="form-section">
                <h2>Chatbot Configuration</h2>

                <div className="form-section-columns">
                  <div className="form-group">
                    <label htmlFor="provider">Provider *</label>
                    <div className="custom-select-wrapper">
                      <select
                        id="provider"
                        name="provider"
                        value={formData.provider}
                        onChange={handleInputChange}
                        required
                        className="custom-select"
                      >
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                        <option value="google">Google</option>
                        <option value="perplexity">Perplexity</option>
                      </select>
                      <img
                        src={downCaret}
                        alt=""
                        className="custom-select-caret"
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="model">Model *</label>
                    <div className="custom-select-wrapper">
                      <select
                        id="model"
                        name="model"
                        value={formData.model}
                        onChange={handleInputChange}
                        required
                        className="custom-select"
                      >
                        <option value="">Select a model</option>
                        {availableModels
                          .filter(
                            (model) =>
                              model.provider === formData.provider &&
                              (model.public === undefined ||
                                model.public === true)
                          )
                          .map((modelOption) => (
                            <option key={modelOption._id} value={modelOption.id}>
                              {modelOption.name}
                            </option>
                          ))}
                      </select>
                      <img
                        src={downCaret}
                        alt=""
                        className="custom-select-caret"
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="range-label-container">
                      <label htmlFor="temperature">Temperature *</label>
                      {formData.temperature !== initialTemperature && (
                        <button
                          type="button"
                          className="range-reset-btn"
                          onClick={() => setFormData(prev => ({ ...prev, temperature: initialTemperature }))}
                          title="Reset to default (1.0)"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <div className="range-input-container">
                      <input
                        type="range"
                        id="temperature"
                        name="temperature"
                        min="0"
                        max="2"
                        step="0.01"
                        value={formData.temperature}
                        onChange={handleInputChange}
                        disabled={
                          formData.model && (
                            (formData.model.toLowerCase().includes('gpt-5') && !formData.model.toLowerCase().includes('gpt-5.2')) ||
                            formData.model.toLowerCase() === 'o1' ||
                            formData.model.toLowerCase().includes('o1-')
                          )
                        }
                        style={{
                          background: `linear-gradient(to right, var(--create-primary) 0%, var(--create-primary) ${(formData.temperature / 2) * 100}%, var(--create-border-light) ${(formData.temperature / 2) * 100}%, var(--create-border-light) 100%)`
                        }}
                      />
                      <span className="range-value">
                        {formData.temperature.toFixed(2)}
                      </span>
                    </div>
                    {formData.model && (
                      (formData.model.toLowerCase().includes('gpt-5') && !formData.model.toLowerCase().includes('gpt-5.2')) ||
                      formData.model.toLowerCase() === 'o1' ||
                      formData.model.toLowerCase().includes('o1-')
                    ) && (
                      <span className="field-note">
                        Temperature is fixed at 1.0 for this model
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="prompt">System Prompt *</label>
                  <textarea
                    id="prompt"
                    name="prompt"
                    value={formData.prompt}
                    onChange={handleInputChange}
                    placeholder="Enter system prompt"
                    rows="5"
                    required
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="folder">Google Drive Folder URL</label>
                  <input
                    type="text"
                    id="folder"
                    name="folder"
                    value={formData.folder}
                    onChange={handleInputChange}
                    placeholder="Paste a Google Drive folder URL to sync files"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Starter Questions</label>
                  <div className="tag-input-container">
                    <input
                      type="text"
                      id="questionInput"
                      value={questionInput}
                      onChange={(e) => setQuestionInput(e.target.value)}
                      placeholder="Add a starter question and press Enter"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddQuestion();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddQuestion}
                      className="add-button"
                    >
                      Add
                    </button>
                  </div>
                  <div className="questions-container">
                    {formData.starter_questions.map((question, index) => (
                      <div key={index} className="question-item">
                        <span>{question}</span>
                        <button
                          type="button"
                          className="remove-question"
                          onClick={() => handleRemoveQuestion(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Functions</label>
                  <FunctionSelector
                    selectedFunctions={formData.functions}
                    onChange={handleFunctionChange}
                  />
                </div>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-button"
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create Tool"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreatePage;
